import Hero from "../components/Hero";
import hotelImg from "../assests/images/hotel-bg.jpg";

import {
  FaHotel,
  FaPlane,
  FaCar,
  FaMapMarkedAlt,
  FaHeadset,
  FaShieldAlt,
  FaUtensils,
  FaPassport,
  FaArrowRight,
  FaCheck,
} from "react-icons/fa";

import CTASection from "../components/CTASection";
import { Helmet } from "react-helmet-async";

export default function Services() {
  const services = [
    {
      icon: <FaHotel />,
      number: "01",
      title: "Hotel Booking",
      desc: "Book carefully selected hotels at competitive prices with a simple and reliable booking experience.",
      link: "https://marinadubai.com/",
    },
    {
      icon: <FaPlane />,
      number: "02",
      title: "Flight Booking",
      desc: "Find convenient and affordable flight options for your next journey with flexible travel choices.",
      link: "https://southtravels.com/flights",
      popular: true,
    },
    {
      icon: <FaPassport />,
      number: "03",
      title: "Visa Services",
      desc: "Get professional visa assistance, document guidance and support throughout your application process.",
      link: "https://southtravels.com/",
      popular: true,
    },
    {
      icon: <FaMapMarkedAlt />,
      number: "04",
      title: "Tour Packages",
      desc: "Discover curated tours and travel experiences designed around popular destinations and attractions.",
      link: "https://tourismindubai.com/",
    },
    {
      icon: <FaUtensils />,
      number: "05",
      title: "Food & Dining",
      desc: "Explore memorable dining experiences and discover restaurants for every taste and occasion.",
      link: "https://dubaicitytour.com/",
    },
    {
      icon: <FaCar />,
      number: "06",
      title: "Car Rentals",
      desc: "Choose from convenient car rental options to make getting around Dubai comfortable and easy.",
      link: "https://southtravels.com/car-rental-from-dubai",
    },
  ];

  const features = [
    {
      icon: <FaShieldAlt />,
      title: "Secure & Reliable",
      desc: "Your booking experience is designed with security and reliability in mind.",
    },
    {
      icon: <FaHeadset />,
      title: "24/7 Assistance",
      desc: "Our support team is available to help whenever you need assistance.",
    },
    {
      icon: <FaHotel />,
      title: "Trusted Hotels",
      desc: "Discover carefully selected hotels in popular destinations and locations.",
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Travel Made Easy",
      desc: "Bring hotels, travel services and experiences together in one place.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Travel & Hotel Services | Beach Hotels Dubai</title>

        <meta
          name="description"
          content="Explore affordable hotel booking, budget travel planning, holiday packages, visa assistance, car rentals and travel services with Beach Hotels Dubai."
        />

        <link
          rel="canonical"
          href="https://marinadubai.com/services"
        />

        <meta
          property="og:title"
          content="Travel & Hotel Services | Beach Hotels Dubai"
        />

        <meta
          property="og:description"
          content="Explore hotel bookings, flights, visa services, tours, dining and car rentals with Beach Hotels Dubai."
        />

        <meta
          property="og:url"
          content="https://marinadubai.com/services"
        />

        <meta property="og:type" content="website" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Travel & Hotel Services | Beach Hotels Dubai"
        />

        <meta
          name="twitter:description"
          content="Explore hotel bookings, flights, visa services, tours, dining and car rentals with Beach Hotels Dubai."
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <main className="min-h-screen bg-[#F7F5EF] text-slate-900">

        {/* =========================================================
            HERO
        ========================================================== */}

        <Hero
          variant="inner"
          title="Our Services"
          subtitle="Everything you need for a smoother, smarter and more enjoyable journey."
          backgroundImage={hotelImg}
        />

        {/* =========================================================
            INTRO
        ========================================================== */}

        <section className="w-full bg-[#F7F5EF] py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            <div className="mx-auto max-w-3xl text-center">

              {/* Label */}
              <div className="inline-flex items-center gap-2 rounded-full border border-[#0B5552]/10 bg-white px-4 py-2 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-[#D4A83F]" />

                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0B5552] sm:text-xs">
                  Everything In One Place
                </span>
              </div>

              {/* Heading */}
              <h2 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight text-[#123331] sm:text-4xl lg:text-5xl">
                Travel Services Designed
                <span className="block text-[#0B5552]">
                  Around You
                </span>
              </h2>

              {/* Description */}
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base lg:text-lg">
                From finding the right hotel to arranging flights,
                visas, tours and transportation, our services help
                make your travel planning simple and stress-free.
              </p>

            </div>
          </div>
        </section>

        {/* =========================================================
            SERVICES
        ========================================================== */}

        <section className="w-full bg-[#F7F5EF] pb-20 sm:pb-24 lg:pb-28">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            {/* Section heading */}
            <div className="mb-10 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A83F] sm:text-sm">
                  What We Offer
                </p>

                <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-[#123331] sm:text-4xl">
                  Services For Every Journey
                </h2>
              </div>

              <p className="max-w-md text-sm leading-6 text-slate-500 sm:text-base">
                Explore our range of travel services and choose
                exactly what you need for your next trip.
              </p>

            </div>

            {/* Service cards */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">

              {services.map((service, index) => (
                <a
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={index}
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-[28px]
                    border
                    border-[#0B5552]/10
                    bg-white
                    p-6
                    shadow-[0_8px_35px_rgba(11,85,82,0.06)]
                    transition-all
                    duration-500
                    hover:-translate-y-2
                    hover:border-[#0B5552]/20
                    hover:shadow-[0_22px_55px_rgba(11,85,82,0.14)]
                    sm:p-7
                  "
                >

                  {/* Decorative circle */}
                  <div
                    className="
                      absolute
                      -right-16
                      -top-16
                      h-44
                      w-44
                      rounded-full
                      bg-[#F0F6F4]
                      transition-all
                      duration-500
                      group-hover:scale-110
                      group-hover:bg-[#0B5552]/10
                    "
                  />

                  {/* Decorative small circle */}
                  <div
                    className="
                      absolute
                      -bottom-8
                      -right-8
                      h-20
                      w-20
                      rounded-full
                      border
                      border-[#D4A83F]/10
                      transition
                      duration-500
                      group-hover:scale-125
                    "
                  />

                  {/* Popular badge */}
                  {service.popular && (
                    <div
                      className="
                        absolute
                        right-5
                        top-5
                        z-10
                        rounded-full
                        bg-[#FFF5D9]
                        px-3
                        py-1.5
                        text-[9px]
                        font-extrabold
                        uppercase
                        tracking-[0.14em]
                        text-[#96721B]
                      "
                    >
                      Popular
                    </div>
                  )}

                  {/* Number */}
                  <div
                    className="
                      absolute
                      right-6
                      top-16
                      z-10
                      text-5xl
                      font-black
                      leading-none
                      text-slate-100
                      transition-all
                      duration-500
                      group-hover:text-[#0B5552]/10
                    "
                  >
                    {service.number}
                  </div>

                  {/* Icon */}
                  <div
                    className="
                      relative
                      z-10
                      flex
                      h-16
                      w-16
                      items-center
                      justify-center
                      rounded-2xl
                      bg-[#EAF3F1]
                      text-xl
                      text-[#0B5552]
                      shadow-sm
                      transition-all
                      duration-500
                      group-hover:rotate-3
                      group-hover:bg-[#0B5552]
                      group-hover:text-white
                      group-hover:shadow-lg
                    "
                  >
                    {service.icon}
                  </div>

                  {/* Content */}
                  <div className="relative z-10 mt-7">

                    <h3
                      className="
                        text-xl
                        font-extrabold
                        tracking-tight
                        text-[#123331]
                        transition-colors
                        duration-300
                        group-hover:text-[#0B5552]
                      "
                    >
                      {service.title}
                    </h3>

                    <p
                      className="
                        mt-3
                        min-h-[72px]
                        text-sm
                        leading-6
                        text-slate-500
                      "
                    >
                      {service.desc}
                    </p>

                    {/* Link */}
                    <div
                      className="
                        mt-7
                        flex
                        items-center
                        justify-between
                        border-t
                        border-slate-100
                        pt-5
                      "
                    >
                      <span className="text-sm font-bold text-[#0B5552]">
                        Explore Service
                      </span>

                      <span
                        className="
                          flex
                          h-9
                          w-9
                          items-center
                          justify-center
                          rounded-full
                          bg-[#F3F6F5]
                          text-[#0B5552]
                          transition-all
                          duration-300
                          group-hover:translate-x-1
                          group-hover:bg-[#0B5552]
                          group-hover:text-white
                        "
                      >
                        <FaArrowRight className="text-xs" />
                      </span>
                    </div>

                  </div>
                </a>
              ))}

            </div>
          </div>
        </section>

        {/* =========================================================
            WHY CHOOSE US
        ========================================================== */}

        <section className="relative w-full overflow-hidden bg-[#073F3D] py-20 sm:py-24 lg:py-28">

          {/* Decorative background */}
          <div
            className="
              absolute
              -right-32
              -top-32
              h-96
              w-96
              rounded-full
              bg-[#0B5552]
              opacity-60
              blur-3xl
            "
          />

          <div
            className="
              absolute
              -bottom-40
              -left-32
              h-96
              w-96
              rounded-full
              bg-[#D4A83F]
              opacity-10
              blur-3xl
            "
          />

          {/* Grid decoration */}
          <div
            className="
              absolute
              inset-0
              opacity-[0.035]
              [background-image:linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)]
              [background-size:50px_50px]
            "
          />

          <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            {/* Heading */}
            <div className="max-w-2xl">

              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-white/10
                  bg-white/10
                  px-4
                  py-2
                "
              >
                <span className="h-2 w-2 rounded-full bg-[#D4A83F]" />

                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4A83F] sm:text-xs">
                  Why Travel With Us
                </span>
              </div>

              <h2
                className="
                  !text-white
                  mt-5
                  text-3xl
                  font-extrabold
                  leading-tight
                  tracking-tight
                  sm:text-4xl
                  lg:text-5xl
                "
              >
                More Than Just
                <span className="block text-[#D4A83F]">
                  Travel Services
                </span>
              </h2>

              <p className="mt-5 max-w-xl text-sm leading-7 text-white/60 sm:text-base">
                We focus on making every part of your travel
                experience easier, more comfortable and more reliable.
              </p>

            </div>

            {/* Feature cards */}
            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">

              {features.map((item, index) => (
                <div
                  key={index}
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-[26px]
                    border
                    border-white/10
                    bg-white/[0.055]
                    p-6
                    backdrop-blur-sm
                    transition-all
                    duration-500
                    hover:-translate-y-2
                    hover:bg-white/[0.09]
                    hover:border-white/20
                  "
                >

                  {/* Number */}
                  <span
                    className="
                      absolute
                      right-5
                      top-5
                      text-4xl
                      font-black
                      text-white/[0.04]
                    "
                  >
                    0{index + 1}
                  </span>

                  {/* Icon */}
                  <div
                    className="
                      flex
                      h-12
                      w-12
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#D4A83F]/10
                      text-lg
                      text-[#D4A83F]
                      transition-all
                      duration-500
                      group-hover:bg-[#D4A83F]
                      group-hover:text-[#073F3D]
                    "
                  >
                    {item.icon}
                  </div>

                  <h3 className="mt-6 text-lg font-extrabold text-white">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-white/55">
                    {item.desc}
                  </p>

                  <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-white/40">
                    <FaCheck className="text-[#D4A83F]" />
                    <span>Travel with confidence</span>
                  </div>

                </div>
              ))}

            </div>

            {/* Bottom trust line */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-7">

              <div className="flex items-center gap-2 text-xs font-semibold text-white/50">
                <FaCheck className="text-[#D4A83F]" />
                Trusted travel services
              </div>

              <div className="flex items-center gap-2 text-xs font-semibold text-white/50">
                <FaCheck className="text-[#D4A83F]" />
                Reliable assistance
              </div>

              <div className="flex items-center gap-2 text-xs font-semibold text-white/50">
                <FaCheck className="text-[#D4A83F]" />
                Convenient booking
              </div>

            </div>

          </div>
        </section>

        {/* =========================================================
            CTA
        ========================================================== */}

        <CTASection />

      </main>
    </>
  );
}